import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectEmployerFromContext, selectLoadedPlatform, selectIsLoginDefaultStep, selectJobType, } from '../../state/slices/slice-link-api';
import { defaultSearchState } from '../../utils/navigation';
import { useOpts } from '../../context';
import getPlatformTypeName from '../../consts/platform-types';
import { getWriteJobFromToken, tokenHasJob } from '../../utils';
import shouldShowEmployerMapping from '../../utils/should-show-employer-mapping';
import { useSandbox } from '../../hooks/useSandbox';
import useLinkNavigate from '../../hooks/useLinkNavigate';
import { useGetLocalizedString } from '../../hooks/useGetLocalizedString';
function useJobForm(template) {
    var _a;
    var title = template.title, subtitle = template.subtitle, textList = template.textList, fields = template.fields;
    var opts = useOpts();
    var navigate = useLinkNavigate();
    var sandboxResult = useSandbox(fields);
    var getLocalizedString = useGetLocalizedString();
    var linkTokenPayload = opts.linkTokenPayload, featureFlags = opts.featureFlags;
    var loadedPlatform = useSelector(selectLoadedPlatform);
    var loadedEmployer = useSelector(selectEmployerFromContext);
    var isDefaultStep = useSelector(selectIsLoginDefaultStep);
    var currentJob = useSelector(selectJobType);
    var accountId = linkTokenPayload.accountId, platformType = linkTokenPayload.platformType;
    var localizedPlatformType = getLocalizedString(getPlatformTypeName(platformType));
    var defaultTitle = useMemo(function () {
        if (accountId)
            return getLocalizedString('WELCOME_BACK');
        return shouldShowEmployerMapping({ loadedPlatform: loadedPlatform, loadedEmployer: loadedEmployer })
            ? getLocalizedString('LOGIN_TO_EMPLOYER', [
                loadedEmployer.name,
                loadedPlatform.name,
                localizedPlatformType,
            ])
            : getLocalizedString('LOGIN_USING_ACCOUNT', [loadedPlatform.name]);
    }, [
        accountId,
        getLocalizedString,
        loadedEmployer,
        loadedPlatform,
        localizedPlatformType,
    ]);
    var actionText = useMemo(function () {
        if (featureFlags.cashAppCopyChanges &&
            tokenHasJob(linkTokenPayload, 'shifts')) {
            return getLocalizedString('GET_INSTANT_PAYCHECK');
        }
        if (getWriteJobFromToken(linkTokenPayload)) {
            return getLocalizedString('UPDATE_YOUR_DIRECT');
        }
        return getLocalizedString('CONNECT_YOUR', [localizedPlatformType]);
    }, [
        linkTokenPayload,
        getLocalizedString,
        localizedPlatformType,
        featureFlags.cashAppCopyChanges,
    ]);
    var defaultIntroBody = useMemo(function () { return getLocalizedString('LOGIN_USING_CREDENTIALS', [actionText]); }, [getLocalizedString, actionText]);
    var notYourPayrollAction = {
        actionType: 'callback',
        label: getLocalizedString('NOT_YOUR_PAYROLL'),
        callback: function () { return navigate(defaultSearchState(linkTokenPayload.platformType)); },
    };
    if (currentJob === 'login') {
        var showWrongPayrollAction = 
        // We only want to show this prompt on default step
        isDefaultStep &&
            // We do not want to show the prompt if an account id is present because
            // an account id indicates that the platform must be correct
            !linkTokenPayload.accountId &&
            // We do not want to show the prompt if the client created their own search
            !featureFlags.usingCustomSearch &&
            // We want to show this prompt if an employer was selected in search OR an employer
            // was in the link token because our employer/platform mapping may be incorrect.
            loadedEmployer &&
            // We want to not show this prompt if the employer that was selected is also
            // a platform (e.g. Chipotle).
            !loadedPlatform.isEmployer;
        return {
            title: isDefaultStep && loadedPlatform.key !== 'adpPortalAlt' && !title
                ? defaultTitle
                : title,
            subtitle: isDefaultStep && loadedPlatform.key !== 'adpPortalAlt' && !subtitle
                ? defaultIntroBody
                : subtitle || null,
            textList: textList,
            actions: showWrongPayrollAction ? [notYourPayrollAction] : [],
            initialValues: (_a = sandboxResult === null || sandboxResult === void 0 ? void 0 : sandboxResult.prefilledFormData) !== null && _a !== void 0 ? _a : {},
        };
    }
    return { title: title, subtitle: subtitle, textList: textList, actions: [] };
}
export default useJobForm;
